import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Check, Pencil, Plus } from "lucide-react";
import { Button, Field, Input, Textarea } from "@/design/components";
import { useEditZeusProject, useCreateMilestone } from "@/domain/useProjectCreation";
import { useUpdateMilestone } from "@/domain/useMutations";
import { useMilestones } from "@/domain/hooks";

// ZEUS-143 — direct "Edit project" modal, stratus_admin only (the server
// enforces this; the caller decides whether to render the trigger at all —
// see ProjectManagementView.tsx). Reuses the already-existing
// useEditZeusProject hook (built for CreateProjectModal's own post-create
// region/district/budget follow-up patch) — no new backend or hook work
// needed here, just a standalone, re-openable entry point for an EXISTING
// project. Applies directly, no review cycle (contrast: contractors submit
// an edit REQUEST instead, reviewed by oversight — see
// RequestProjectEditModal).

export function EditProjectModal({
  open,
  onOpenChange,
  projectId,
  initialValues,
  onEdited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Raw id — real numeric-string in real-API mode, demo id in demo mode. */
  projectId: string;
  initialValues: {
    name: string;
    description?: string;
    region?: string;
    district?: string;
    budgeted?: number;
  };
  onEdited?: () => void;
}) {
  const { editZeusProject, isPending, error } = useEditZeusProject();
  const milestones = useMilestones(projectId);
  const { update: updateMilestone, isPending: savingMilestone } = useUpdateMilestone();
  const { createMilestone, isPending: addingMilestone } = useCreateMilestone();

  const [name, setName] = useState(initialValues.name);
  const [description, setDescription] = useState(initialValues.description ?? "");
  const [region, setRegion] = useState(initialValues.region ?? "");
  const [district, setDistrict] = useState(initialValues.district ?? "");
  const [budgeted, setBudgeted] = useState(initialValues.budgeted != null ? String(initialValues.budgeted) : "");
  const [justification, setJustification] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [editingMilestoneId, setEditingMilestoneId] = useState<string | null>(null);
  const [milestoneEdit, setMilestoneEdit] = useState({ targetDate: "", plannedBudget: "" });
  const [newMilestone, setNewMilestone] = useState({ description: "", targetDate: "", plannedBudget: "" });

  function startEditMilestone(id: string, targetDate: string, plannedBudget?: number) {
    setEditingMilestoneId(id);
    setMilestoneEdit({ targetDate, plannedBudget: plannedBudget != null ? String(plannedBudget) : "" });
  }

  async function saveMilestoneEdit(id: string) {
    await updateMilestone(id, {
      targetDate: milestoneEdit.targetDate || undefined,
      plannedBudget: milestoneEdit.plannedBudget.trim() ? Number(milestoneEdit.plannedBudget.trim()) : undefined,
    });
    setEditingMilestoneId(null);
  }

  async function addNewMilestone() {
    if (!newMilestone.description.trim()) return;
    const ok = await createMilestone(projectId, {
      milestonenumber: `M${milestones.length + 1}`,
      description: newMilestone.description.trim(),
      targetDate: newMilestone.targetDate || undefined,
      plannedBudget: newMilestone.plannedBudget.trim() ? Number(newMilestone.plannedBudget.trim()) : undefined,
    });
    if (ok) setNewMilestone({ description: "", targetDate: "", plannedBudget: "" });
  }

  // Re-sync when opened — the modal instance is reused across opens rather
  // than remounted, and the project may have changed since the last time.
  useEffect(() => {
    if (!open) return;
    setName(initialValues.name);
    setDescription(initialValues.description ?? "");
    setRegion(initialValues.region ?? "");
    setDistrict(initialValues.district ?? "");
    setBudgeted(initialValues.budgeted != null ? String(initialValues.budgeted) : "");
    setJustification("");
    setSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const canSubmit = name.trim().length > 0 && justification.trim().length > 0;

  function reset() {
    setName(initialValues.name);
    setDescription(initialValues.description ?? "");
    setRegion(initialValues.region ?? "");
    setDistrict(initialValues.district ?? "");
    setBudgeted(initialValues.budgeted != null ? String(initialValues.budgeted) : "");
    setJustification("");
    setSubmitted(false);
  }

  async function onSubmit() {
    if (!canSubmit) return;

    const result = await editZeusProject(projectId, {
      name: name.trim(),
      description: description.trim() || undefined,
      region: region.trim() || undefined,
      district: district.trim() || undefined,
      budgeted: budgeted.trim() ? Number(budgeted.trim()) : undefined,
      justification: justification.trim(),
    });
    if (!result) return;

    setSubmitted(true);
    onEdited?.();
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted ? "Project updated" : "Edit project"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span><strong>{name}</strong> has been updated.</span>
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                Applies immediately — no review cycle for a direct oversight edit.
              </Dialog.Description>

              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>

              <Field label="Description (optional)">
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Region (optional)">
                  <Input value={region} onChange={(e) => setRegion(e.target.value)} />
                </Field>
                <Field label="District (optional)">
                  <Input value={district} onChange={(e) => setDistrict(e.target.value)} />
                </Field>
              </div>

              <Field label="Budget, UGX (optional)">
                <Input
                  type="number"
                  value={budgeted}
                  onChange={(e) => setBudgeted(e.target.value)}
                  placeholder="e.g. 2600000000"
                />
              </Field>

              <Field label="Milestones">
                <div className="space-y-2">
                  {milestones.map((m) =>
                    editingMilestoneId === m.id ? (
                      <div key={m.id} className="rounded-md border border-line p-2">
                        <p className="mb-2 text-sm font-medium text-ink">{m.name}</p>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={milestoneEdit.targetDate}
                            onChange={(e) => setMilestoneEdit((v) => ({ ...v, targetDate: e.target.value }))}
                          />
                          <Input
                            type="number"
                            value={milestoneEdit.plannedBudget}
                            onChange={(e) => setMilestoneEdit((v) => ({ ...v, plannedBudget: e.target.value }))}
                            placeholder="Budget, UGX"
                          />
                        </div>
                        <div className="mt-2 flex justify-end gap-2">
                          <Button variant="ghost" onClick={() => setEditingMilestoneId(null)}>Cancel</Button>
                          <Button disabled={savingMilestone} onClick={() => saveMilestoneEdit(m.id)}>
                            {savingMilestone ? "Saving…" : "Save"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div key={m.id} className="flex items-center justify-between gap-2 rounded-md border border-line p-2">
                        <div>
                          <p className="text-sm font-medium text-ink">{m.name}</p>
                          <p className="text-xs text-faint">
                            {m.targetDate || "No target date"}
                            {m.plannedBudget != null ? ` · UGX ${m.plannedBudget.toLocaleString()}` : ""}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          onClick={() => startEditMilestone(m.id, m.targetDate, m.plannedBudget)}
                        >
                          <Pencil className="h-4 w-4" /> Edit
                        </Button>
                      </div>
                    ),
                  )}

                  <div className="rounded-md border border-dashed border-line p-2">
                    <Input
                      value={newMilestone.description}
                      onChange={(e) => setNewMilestone((v) => ({ ...v, description: e.target.value }))}
                      placeholder="New milestone, e.g. Foundation complete"
                    />
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={newMilestone.targetDate}
                        onChange={(e) => setNewMilestone((v) => ({ ...v, targetDate: e.target.value }))}
                      />
                      <Input
                        type="number"
                        value={newMilestone.plannedBudget}
                        onChange={(e) => setNewMilestone((v) => ({ ...v, plannedBudget: e.target.value }))}
                        placeholder="Budget, UGX"
                      />
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button
                        variant="secondary"
                        disabled={addingMilestone || !newMilestone.description.trim()}
                        onClick={addNewMilestone}
                      >
                        <Plus className="h-4 w-4" /> {addingMilestone ? "Adding…" : "Add milestone"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Field>

              <Field label="Justification">
                <Textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={3}
                  placeholder="Why is this edit necessary?"
                />
              </Field>

              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSubmit || isPending} onClick={onSubmit}>
                <Pencil className="h-4 w-4" /> {isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
